// @react
import React, { useState } from 'react'
// @types
import type { ReactElement } from 'react'
import type { MemberBenefitTab } from 'modules/benefitsCenter/types'
// @libraries
import { Grid } from '@material-ui/core'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
// @components
import Box from '@bit/atd.web.box'
import Spacer from '@bit/atd.web.spacer'
import colors from '@bit/atd.web.colors'
// @module common components
import CustomCardHit from 'modules/search/components/Widgets/CustomCardHit'
// @module components
import MetaInfo from 'modules/benefitsCenter/components/MemberBenefits/MetaInfo'
import Courses from 'modules/benefitsCenter/components/MemberBenefits/Courses'
import BadgedLabel from '../BadgedLabel'
// @config
import { tabIndexes, callCustomerCodes, tabsConfig } from '../benefitsConfig'

const useStyles = makeStyles({
  boxRoot: {
    backgroundColor: 'transparent !important',
    border: 'none',
    boxShadow: 'none',
  },
  boxBody: {
    paddingBottom: '0 !important',
  },
  tabsHeader: {
    borderBottom: `1px solid ${colors.gray400}`,
    '& .MuiTabs-indicator': {
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      '& > span': {
        borderTopLeftRadius: '3px',
        borderTopRightRadius: '3px',
        backgroundColor: colors.ctaPrimary,
        width: 'calc(100%)',
      },
    },
    '& button': {
      paddingLeft: '0px',
      paddingRight: '0px',
      marginRight: '32px',
    },
  },
  label: {
    fontSize: '18px',
    fontWeight: 600,
  },
  cardsContainer: {
    marginTop: '24px',
    marginBottom: '40px',
    '& footer': {
      backgroundColor: colors.gray200,
    },
  },
  cardsContainerLast: {
    marginBottom: 0,
  },
})

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel = (props: TabPanelProps): ReactElement => {
  /******************************************
   * PROPS
   ******************************************/

  const { children, value, index, ...rest } = props

  /******************************************
   * RENDERS
   ******************************************/

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...rest}
    >
      {<div style={{ marginTop: '32px' }}>{children}</div>}
    </div>
  )
}

type PropTypes = {
  tabsItems: MemberBenefitTab[]
  productCode: string
}

const DesktopTabs = ({ tabsItems, productCode }: PropTypes) => {
  /******************************************
   * STATES
   ******************************************/

  const [currentTab, setCurrentTab] = useState(tabIndexes.PUBLICATIONS)

  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()

  /******************************************
   * FUNCTIONS
   ******************************************/

  const handleChange = (
    _: React.ChangeEvent<Record<string, unknown>>,
    newValue: number,
  ) => {
    setCurrentTab(newValue)
  }

  /******************************************
   * RENDERS
   ******************************************/

  const isCallCustomerType =
    productCode && callCustomerCodes.includes(productCode)

  const renderLabel = (tab: { tabIndex: number; label: string }) => {
    const labelNode = <div className={classes.label}>{tab.label}</div>
    return tab.tabIndex === tabIndexes.COURSES && isCallCustomerType ? (
      <BadgedLabel label={labelNode} badgeText="UPGRADE" />
    ) : (
      labelNode
    )
  }

  return (
    <Box
      variant={'simple'}
      header={{
        title: 'Member Benefits',
      }}
      classNames={{ root: classes.boxRoot, body: classes.boxBody }}
    >
      {/* Tab Header */}
      <Tabs
        className={classes.tabsHeader}
        value={currentTab}
        onChange={handleChange}
        TabIndicatorProps={{ children: <span /> }}
      >
        {tabsItems.map(({ tab, tabIndex }) => {
          return (
            <Tab
              disableRipple
              key={tabIndex}
              value={tabIndex}
              tabIndex={tabIndex}
              label={renderLabel({
                tabIndex: Number(tab),
                label: tabsConfig[tab].tabName,
              })}
            />
          )
        })}
        <Tab
          disableRipple
          key={tabIndexes.COURSES}
          value={tabIndexes.COURSES}
          tabIndex={tabIndexes.COURSES}
          label={renderLabel({
            tabIndex: Number(tabIndexes.COURSES),
            label: 'Courses',
          })}
        />
      </Tabs>

      {/* Tab Content */}
      {tabsItems.map(({ tabIndex, tab, data }) => {
        return (
          <div key={tabIndex}>
            {/* Tab Panel - List Items */}
            <TabPanel key={tabIndex} value={currentTab} index={tabIndex}>
              {Object.keys(data).map((key, index) => {
                const item = data[key]
                if (!item) return null
                const { title, description, link } = tabsConfig[tab].requests[
                  key
                ]

                return (
                  <div key={key}>
                    {/* Meta */}
                    <MetaInfo
                      title={title}
                      amount={item.meta?.count}
                      description={description}
                      link={link}
                    />

                    {/* Content Cards */}
                    {item.data && item.data?.length > 0 ? (
                      <div
                        className={clsx(classes.cardsContainer, {
                          [classes.cardsContainerLast]:
                            Object.keys(data).length === index + 1,
                        })}
                      >
                        <Grid container spacing={3}>
                          {item.data?.map((hit: any) => (
                            <CustomCardHit
                              key={hit.id}
                              hit={{ ...hit, objectID: hit.id }}
                              index={{ targetedIndex: index }}
                            />
                          ))}
                        </Grid>
                      </div>
                    ) : (
                      Object.keys(data).length !== index + 1 && (
                        <Spacer size={32} />
                      )
                    )}
                  </div>
                )
              })}
            </TabPanel>
          </div>
        )
      })}

      {/* Tab Panel - Courses */}
      <TabPanel key="courses" value={currentTab} index={tabIndexes.COURSES}>
        <Courses variant={isCallCustomerType ? 'callCustomer' : 'launchLMS'} />
      </TabPanel>
    </Box>
  )
}

export default DesktopTabs
